import React, { useEffect, useState } from 'react';
import { Box, useToast } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';

import { WattwatchersMeter } from 'clipsal-cortex-types/src/api/api-ww-meter';
import { CenteredLoader } from 'clipsal-cortex-ui/src/components/CenteredLoader';
import { WATTWATCHERS_MANUFACTURER_ID } from 'clipsal-cortex-utils/src/constants/common-constants';

import { Appliance } from '../../../api/api-appliance';
import { InstalledDevice } from '../../../api/api-device';
import { get, patch } from '../../../api/api-helpers';
import { useAppDispatch } from '../../../app/hooks';
import { useSiteRouteChangeContext } from '../../../context/context-exports';
import { setHasUnsavedChanges } from '../../wizard/wizardSlice';
import { WizardSubRouteCardWrapper } from '../../wizard/WizardSubRouteCardWrapper';
import { SiteRouteChangeContextProps } from '../Site';
import { useSite } from '../siteApi';
import {
  fetchAssignments,
  selectAssignments,
  setBatteries,
  setEVChargers,
  setInverters,
} from '../system-details/systemDetailsSlice';
import { checkMeterPhasing } from './meter-phasing-helpers';
import { useSiteMeters } from './meterSetupApi';
import { addMeters, selectRawMeters, setAppliances } from './meterSetupSlice';

export function MeterSetup() {
  const { onMoveForward } = useSiteRouteChangeContext();
  const isLoaded = useFetchMeters();

  return (
    <>
      {!isLoaded ? (
        <MeterSetupCenteredLoader />
      ) : (
        <Box>
          <Outlet context={{ onMoveForward } satisfies SiteRouteChangeContextProps} />
        </Box>
      )}
    </>
  );
}

function MeterSetupCenteredLoader() {
  const { pathname } = useLocation();
  const isOnMeterSetupSubRoute = /\/meter_setup\/configure\//.test(pathname);

  // Wizard Stepper is hidden on meter setup sub routes, so we need to override padding to the top of the wizard
  const wizardCardProps = isOnMeterSetupSubRoute
    ? {
        containerProps: { pt: 0, mt: 0 },
        p: [0],
      }
    : undefined;

  return (
    <WizardSubRouteCardWrapper {...wizardCardProps}>
      <CenteredLoader minH={300} />
    </WizardSubRouteCardWrapper>
  );
}

/**
 * Custom hook which fetches meters for the site and updates the redux store.
 *
 * @returns Boolean specifying whether all data has loaded and the page is ready for user interaction.
 *
 */
function useFetchMeters() {
  const [isLoaded, setLoaded] = useState(false);
  const [hasFetchedOtherDevices, setHasFetchedOtherDevices] = useState(false);
  const dispatch = useAppDispatch();
  const { site } = useSite();
  const assignments = useSelector(selectAssignments);
  const toast = useToast();
  const { siteMeters, isLoadingSiteMeters } = useSiteMeters();
  const meters = useSelector(selectRawMeters);

  useEffect(() => {
    async function fetchAPI() {
      dispatch(setHasUnsavedChanges(false));

      try {
        if (siteMeters.length) {
          // Get all inverters, batteries, EV chargers, appliances and meter data
          const [inverters, batteries, evChargers, appliances, ...wattwatchersMeterData] = await Promise.all([
            get<InstalledDevice[]>(`/fleet/sites/${site.clipsal_solar_id}/inverters`),
            get<InstalledDevice[]>(`/fleet/sites/${site.clipsal_solar_id}/batteries`),
            get<InstalledDevice[]>(`/fleet/sites/${site.clipsal_solar_id}/ev_chargers`),
            get<Appliance[]>(`/fleet/sites/${site.clipsal_solar_id}/appliances`),

            // Only fetch wattwatchers meters
            ...siteMeters
              .filter((m) => m.meta_data.manufacturer_id === WATTWATCHERS_MANUFACTURER_ID)
              .map((meter) => get<WattwatchersMeter>(`/fleet/wattwatchers/installed_meters/${meter.row_id}`)),
          ]);
          setHasFetchedOtherDevices(true);

          // update phasing in meters if required as it needs to match site phasing
          const { isCorrectlyPhased, updatedMeters } = checkMeterPhasing(site.electrical_config, wattwatchersMeterData);
          if (!isCorrectlyPhased) {
            // Update each meter respectively
            const updatedMeterData = await Promise.all(
              updatedMeters.map<Promise<WattwatchersMeter>>((m) =>
                patch(`/fleet/wattwatchers/installed_meters/${m.installed_device_id}`, m)
              )
            );
            toast({
              title: `Meter voltage references updated!`,
              description: 'Circuit voltage references has been updated as per site electrical configuration.',
              status: 'info',
              isClosable: true,
            });
            dispatch(addMeters(updatedMeterData));
          } else {
            dispatch(addMeters(wattwatchersMeterData));
          }

          // update data in redux
          dispatch(setAppliances(appliances));
          dispatch(setInverters(inverters));
          dispatch(setBatteries(batteries));
          dispatch(setEVChargers(evChargers));

          // Cache circuit type options (aka assignments or monitor types)
          if (!assignments.length) await dispatch(fetchAssignments());

          // fetch inverters and batteries if not fetched yet
        } else if (!hasFetchedOtherDevices) {
          // Get all inverters, batteries and EV chargers
          const [inverters, batteries, evChargers] = await Promise.all([
            get<InstalledDevice[]>(`/fleet/sites/${site.clipsal_solar_id}/inverters`),
            get<InstalledDevice[]>(`/fleet/sites/${site.clipsal_solar_id}/batteries`),
            get<InstalledDevice[]>(`/fleet/sites/${site.clipsal_solar_id}/ev_chargers`),
          ]);
          dispatch(setInverters(inverters));
          dispatch(setBatteries(batteries));
          dispatch(setEVChargers(evChargers));
          setHasFetchedOtherDevices(true);
        }
      } catch (e) {
        console.error(e);
        toast({
          title: 'Unable to load meter data.',
          description: 'Note that Solar Analytics meters will not work with this tool.',
          status: 'error',
          isClosable: true,
        });
      }
      setLoaded(true);
    }

    // As this component is wrapped with React Transition group, it is cloned in each route changes as
    // it is required for sliding animation. Hence this is rerendered on every route changes.
    // Thus we have to use redux values to determine if we need to fetch API
    if (!isLoadingSiteMeters && !isLoaded && !Object.keys(meters).length) {
      fetchAPI();
    } else {
      if (!isLoadingSiteMeters && Object.keys(meters).length && !isLoaded) setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingSiteMeters, isLoaded, toast, site.clipsal_solar_id]);

  return isLoaded;
}
